import { ref, watch, onBeforeUnmount, type Ref } from 'vue'
import { clamp, type Zoom, type Dims } from '@/utils/wirepas-topology'

interface DragState<N> { node: N | null; pan: boolean; sx: number; sy: number; moved: boolean }

export interface GraphPanZoomOptions<N> {
  svgEl: Ref<SVGSVGElement | null>
  dims: Ref<Dims>
  onDragStart?: (n: N) => void
  onDrag?: (n: N, x: number, y: number) => void
  onDragEnd?: (n: N) => void
  onSelectNode?: (n: N) => void
  onSelectBackground?: () => void
}

// Screen<->graph coordinate math, wheel-zoom, background-pan and node-drag pointer sequencing.
// Deliberately has no idea what a "node" is beyond what the caller's callbacks need -- all node/
// simulation mutation is delegated back to the caller so this stays reusable for any SVG graph.
export function useGraphPanZoom<N>(opts: GraphPanZoomOptions<N>) {
  const zoom = ref<Zoom>({ k: 1, x: 0, y: 0 })
  const userMoved = ref(false)
  const dragState: DragState<N> = { node: null, pan: false, sx: 0, sy: 0, moved: false }

  const toGraph = (clientX: number, clientY: number) => {
    const rect = opts.svgEl.value!.getBoundingClientRect()
    const sx = ((clientX - rect.left) / rect.width) * opts.dims.value.w
    const sy = ((clientY - rect.top) / rect.height) * opts.dims.value.h
    const z = zoom.value
    return { x: (sx - z.x) / z.k, y: (sy - z.y) / z.k, sx, sy }
  }

  const onWheel = (e: WheelEvent) => {
    e.preventDefault()
    userMoved.value = true
    const { sx, sy } = toGraph(e.clientX, e.clientY)
    const z = zoom.value
    const k2 = clamp(z.k * Math.exp(-e.deltaY * 0.0015), 0.4, 4)
    const gx = (sx - z.x) / z.k, gy = (sy - z.y) / z.k
    zoom.value = { k: k2, x: sx - gx * k2, y: sy - gy * k2 }
  }
  // svgEl only exists once the caller's data has loaded, so it's null at first mount -- attach the
  // (non-passive, so preventDefault works) listener reactively as the element itself mounts/unmounts.
  watch(opts.svgEl, (el, prevEl) => {
    prevEl?.removeEventListener('wheel', onWheel)
    el?.addEventListener('wheel', onWheel, { passive: false })
  })
  onBeforeUnmount(() => opts.svgEl.value?.removeEventListener('wheel', onWheel))

  const onNodeDown = (e: PointerEvent, n: N) => {
    e.stopPropagation()
    try { (e.target as Element).setPointerCapture?.(e.pointerId) } catch { /* no active pointer (e.g. tests) -- ignore */ }
    userMoved.value = true
    const g = toGraph(e.clientX, e.clientY)
    dragState.node = n; dragState.pan = false; dragState.sx = g.sx; dragState.sy = g.sy; dragState.moved = false
    opts.onDragStart?.(n)
  }
  const onSvgDown = (e: PointerEvent) => {
    try { (e.target as Element).setPointerCapture?.(e.pointerId) } catch { /* no active pointer (e.g. tests) -- ignore */ }
    const g = toGraph(e.clientX, e.clientY)
    dragState.node = null; dragState.pan = true; dragState.sx = g.sx; dragState.sy = g.sy; dragState.moved = false
  }
  const onMove = (e: PointerEvent) => {
    if (!dragState.node && !dragState.pan) return
    const g = toGraph(e.clientX, e.clientY)
    if (Math.abs(g.sx - dragState.sx) > 3 || Math.abs(g.sy - dragState.sy) > 3) dragState.moved = true
    if (dragState.node) {
      opts.onDrag?.(dragState.node, g.x, g.y)
    } else if (dragState.pan) {
      userMoved.value = true
      const dx = g.sx - dragState.sx, dy = g.sy - dragState.sy
      zoom.value = { k: zoom.value.k, x: zoom.value.x + dx, y: zoom.value.y + dy }
      dragState.sx = g.sx; dragState.sy = g.sy
    }
  }
  const onUp = () => {
    if (dragState.node) {
      if (!dragState.moved) opts.onSelectNode?.(dragState.node)
      opts.onDragEnd?.(dragState.node)
    } else if (dragState.pan && !dragState.moved) {
      opts.onSelectBackground?.()
    }
    dragState.node = null; dragState.pan = false; dragState.sx = 0; dragState.sy = 0; dragState.moved = false
  }
  const zoomBy = (f: number) => {
    userMoved.value = true
    const z = zoom.value
    const k2 = clamp(z.k * f, 0.4, 4)
    const cx = opts.dims.value.w / 2, cy = opts.dims.value.h / 2
    const gx = (cx - z.x) / z.k, gy = (cy - z.y) / z.k
    zoom.value = { k: k2, x: cx - gx * k2, y: cy - gy * k2 }
  }

  return { zoom, userMoved, onNodeDown, onSvgDown, onMove, onUp, zoomBy }
}
